import { useEffect, useState, useCallback } from "react";

/**
 * Custom hook to guard browser back/forward, tab closing, and page refreshes.
 * @param {boolean} initialDirtyState - Initial dirty state
 * @param {string} confirmMessage - Message shown on history back/forward attempt
 */
export const usePageLeaveGuard = (
  initialDirtyState = false,
  confirmMessage = "You have unsaved changes. Are you sure you want to go back?"
) => {
  const [isDirty, setIsDirty] = useState(initialDirtyState);

  // 1️⃣ Prevent Browser Refresh / Tab Close
  useEffect(() => {
    const handleBeforeUnload = (event) => {
      if (isDirty) {
        event.preventDefault();
        event.returnValue = confirmMessage; // Triggers native browser warning
        return confirmMessage;
      }
    };

    window.addEventListener("beforeunload", handleBeforeUnload);
    return () => window.removeEventListener("beforeunload", handleBeforeUnload);
  }, [isDirty, confirmMessage]);

  // 2️⃣ Prevent Browser Back / Forward Buttons
  useEffect(() => {
    if (!isDirty) return;

    // Push dummy state to capture back button event
    window.history.pushState(null, "", window.location.href);

    const handlePopState = (event) => {
      if (isDirty) {
        const confirmLeave = window.confirm(confirmMessage);
        if (!confirmLeave) {
          // Re-push history entry to stay on current page
          window.history.pushState(null, "", window.location.href);
        } else {
          // User agreed to leave — unguard future transitions
          setIsDirty(false);
        }
      }
    };

    window.addEventListener("popstate", handlePopState);
    return () => window.removeEventListener("popstate", handlePopState);
  }, [isDirty, confirmMessage]);

  return { isDirty, setIsDirty };
};